import React, { createContext, useContext, useState, useEffect } from "react";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from "sweetalert2";

const TaskContext = createContext();
export const useTaskContext = () => useContext(TaskContext);

export const TaskProvider = ({ children }) => {
  const [listsGlobal, setListsGlobal] = useState([]);
  const [tasksGlobal, setTasksGlobal] = useState([]);
  const [groupsGlobal, setGroupsGlobal] = useState([]);
  const [listColors, setListColors] = useState({});

  // Cargar LISTAS (todas)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "list"), (snapshot) => {
      const allLists = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setListsGlobal(allLists);

      // Generar un diccionario nombreLista->color (si todavía usas "name" para "status")
      const colors = {};
      allLists.forEach((l) => {
        colors[l.name] = l.color || "#007bff";
      });
      setListColors(colors);
    });
    return () => unsub();
  }, []);

  // Cargar TAREAS (todas)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "tareas"), (snapshot) => {
      const allTasks = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        comments: doc.data().comments || [],
        attachments: doc.data().attachments || [],
        avatarUrl: doc.data().avatarUrl || "/img/avatar2.jpg",
      }));
      setTasksGlobal(allTasks);
    });
    return () => unsub();
  }, []);

  // Crear / Editar LISTA (con projectId)
  const createOrUpdateList = async ({
    name,
    color,
    projectId,
    oldName = null,
  }) => {
    try {
      if (!name.trim()) {
        Swal.fire({
          title: "El nombre de la lista no puede estar vacío",
          icon: "error",
          scrollbarPadding: false,
        });

        return;
      }
      if (!projectId) {
        Swal.fire("Error", "Falta projectId en la lista", "error");
        return;
      }
      const listsRef = collection(db, "list");

      if (oldName) {
        // Editar (renombrar) la lista
        // Buscar la lista con { oldName, projectId }
        const qSnap = await getDocs(
          query(
            listsRef,
            where("name", "==", oldName),
            where("projectId", "==", projectId)
          )
        );
        if (!qSnap.empty) {
          const listDocRef = qSnap.docs[0].ref;
          await updateDoc(listDocRef, { name, color });

          // Actualizar tareas que tengan status=oldName y projectId
          const tasksRef = collection(db, "tareas");
          const tSnap = await getDocs(
            query(
              tasksRef,
              where("status", "==", oldName),
              where("projectId", "==", projectId)
            )
          );
          const batch = writeBatch(db);
          tSnap.forEach((t) => {
            batch.update(t.ref, { status: name });
          });
          await batch.commit();
          Swal.fire({
            title: "¡Lista actualizada!",
            text: "Renombrada correctamente",
            icon: "success",
            scrollbarPadding: false,
          });
        }
      } else {
        // Crear nueva lista
        // Verificar que no exista
        const qSnap = await getDocs(
          query(
            listsRef,
            where("name", "==", name),
            where("projectId", "==", projectId)
          )
        );
        if (!qSnap.empty) {
          Swal.fire({
            title: "Ya existe una lista con ese nombre en este proyecto",
            icon: "error",
            scrollbarPadding: false,
          });
          return;
        }
        await addDoc(listsRef, { name, color, projectId });
        Swal.fire({
          title: "¡Lista creada!",
          text: "Se agregó correctamente",
          icon: "success",
          scrollbarPadding: false,
        });
      }
    } catch (error) {
      console.error("Error al crear/editar lista:", error);
      Swal.fire("Error", "No se pudo crear/editar la lista", "error");
    }
  };

  // Eliminar LISTA
  const deleteList = async (listName, projectId) => {
    const result = await Swal.fire({
      title: "¿Seguro que quieres eliminar la lista?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      scrollbarPadding: false,
    });
    if (!result.isConfirmed) return;

    try {
      const listsRef = collection(db, "list");
      const qSnap = await getDocs(
        query(
          listsRef,
          where("name", "==", listName),
          where("projectId", "==", projectId)
        )
      );
      if (!qSnap.empty) {
        const docRef = qSnap.docs[0].ref;
        await deleteDoc(docRef);
        Swal.fire({
          title: "¡Eliminada!",
          text: "La lista ha sido eliminada",
          icon: "success",
          scrollbarPadding: false,
        });
      }
    } catch (error) {
      console.error("Error al eliminar lista:", error);
      Swal.fire("Error", "No se pudo eliminar la lista", "error");
    }
  };

  // Crear Tarea
  const createTask = async (taskData) => {
    if (!taskData.projectId) {
      Swal.fire("Error", "Falta projectId en la tarea", "error");
      return;
    }
    // Añadimos createdAt al documento
    const docRef = await addDoc(collection(db, "tareas"), {
      ...taskData,
      createdAt: serverTimestamp(),
    });
    Swal.fire({
      title: "¡Tarea agregada!",
      text: "La nueva tarea se ha agregado correctamente",
      icon: "success",
      scrollbarPadding: false,
    });
    return docRef.id;
  };

  // Editar Tarea
  const updateTask = async (taskId, newData) => {
    try {
      const ref = doc(db, "tareas", taskId);
      await updateDoc(ref, newData);
      Swal.fire({
        icon: "success",
        title: "Tarea actualizada",
        showConfirmButton: false,
        timer: 1500,
        scrollbarPadding: false,
      });
    } catch (err) {
      console.error("Error en updateTask:", err);
    }
  };

  // Eliminar Tarea
  const deleteTask = async (taskId) => {
    const res = await Swal.fire({
      title: "¿Estás seguro?",
      text: "No podrás revertir esto",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      scrollbarPadding: false,
    });
    if (!res.isConfirmed) return;

    await deleteDoc(doc(db, "tareas", taskId));
    Swal.fire({
      title: "¡Eliminado!",
      text: "La tarea ha sido eliminada",
      icon: "success",
      scrollbarPadding: false,
    });
  };

  useEffect(() => {
    // Escucha en tiempo real la colección "gruposTareas"
    const unsub = onSnapshot(collection(db, "gruposTareas"), (snapshot) => {
      const tempGroups = [];

      // Por cada grupo, hacemos también onSnapshot a su subcolección "projects"
      const unsubProjectsArr = snapshot.docs.map((groupDoc) => {
        const groupData = {
          id: groupDoc.id,
          ...groupDoc.data(),
          projects: [],
        };

        // Configuramos onSnapshot para la subcolección "projects" de este grupo
        const projectsRef = collection(
          db,
          "gruposTareas",
          groupDoc.id,
          "projects"
        );
        return onSnapshot(projectsRef, (projSnap) => {
          groupData.projects = projSnap.docs.map((p) => ({
            id: p.id,
            ...p.data(),
          }));

          // Actualiza o inserta groupData en tempGroups
          const existingIndex = tempGroups.findIndex(
            (g) => g.id === groupData.id
          );
          if (existingIndex >= 0) {
            tempGroups[existingIndex] = { ...groupData };
          } else {
            tempGroups.push(groupData);
          }
          // Actualizamos el estado
          setGroupsGlobal([...tempGroups]);
        });
      });

      // Limpieza: si se desmonta, desuscribimos
      return () => {
        unsubProjectsArr.forEach((fn) => fn && fn());
      };
    });

    return () => unsub();
  }, []);

  return (
    <TaskContext.Provider
      value={{
        listsGlobal,
        tasksGlobal,
        listColors,
        createOrUpdateList,
        deleteList,
        createTask,
        groupsGlobal,
        updateTask,
        deleteTask,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};
